/**
 * Deep equality comparison for objects and arrays.
 * Used for dirty state tracking in DataStore.
 *
 * @param a - First value
 * @param b - Second value
 * @returns true if values are deeply equal
 */
export function deepEqual(a: unknown, b: unknown): boolean {
	// Reference equality
	if (a === b) {
		return true;
	}

	// Null/undefined checks
	if (a === null || b === null || a === undefined || b === undefined) {
		return a === b;
	}

	// Type checks
	if (typeof a !== typeof b) {
		return false;
	}

	// Primitive types
	if (typeof a !== "object") {
		return a === b;
	}

	// Array comparison
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) {
			return false;
		}
		for (let i = 0; i < a.length; i++) {
			if (!deepEqual(a[i], b[i])) {
				return false;
			}
		}
		return true;
	}

	// One is array, other is not
	if (Array.isArray(a) || Array.isArray(b)) {
		return false;
	}

	// Date comparison
	if (a instanceof Date && b instanceof Date) {
		return a.getTime() === b.getTime();
	}

	// Object comparison
	const objA = a as Record<string, unknown>;
	const objB = b as Record<string, unknown>;

	const keysA = Object.keys(objA);
	const keysB = Object.keys(objB);

	if (keysA.length !== keysB.length) {
		return false;
	}

	for (const key of keysA) {
		if (!Object.prototype.hasOwnProperty.call(objB, key)) {
			return false;
		}
		if (!deepEqual(objA[key], objB[key])) {
			return false;
		}
	}

	return true;
}
